/** Hero data ported 1:1 from the prototype (source of truth for numbers & texts). */

export interface HeroSkin {
  id: string;
  name: string;
  bg: [string, string];
  acc: string;
}

export interface HeroAi {
  tight: number;
  aggr: number;
  bluff: number;
}

export type AbilityId = 'refund' | 'odds' | 'roar' | 'peek' | 'shield' | 'magnet';

export interface Ability {
  id: AbilityId;
  name: string;
  icon: string;
  desc: string;
}

export interface Hero {
  id: string;
  name: string;
  race: string;
  color: string;
  style: string;
  ai: HeroAi;
  emotes: string[];
  skins: HeroSkin[];
  ability: Ability;
}

export const ABILITIES: Record<string, Ability> = {
  brunhild: { id: 'refund', name: 'Bierdeckel-Rabatt', icon: '🍺', desc: 'Aktiviere während einer Hand: Verlierst du diesen Showdown, bekommst du 15 % des Pots zurück (max. 200 Chips).' },
  faelan: { id: 'odds', name: 'Sternenblick', icon: '✦', desc: 'Aktiviere während einer Hand: Du siehst für den Rest der Hand deine Gewinnwahrscheinlichkeit in Prozent.' },
  grokk: { id: 'roar', name: 'Kriegsschrei', icon: '🗯', desc: 'Aktiviere während einer Hand: Dein nächster Raise schüchtert ein – Gegner folden diese Hand deutlich eher.' },
  pippa: { id: 'peek', name: 'Flinke Finger', icon: '👁', desc: 'Aktiviere während einer Hand: Du linst eine Karte eines zufälligen Gegners ab, der noch in der Hand ist.' },
  aldric: { id: 'shield', name: 'Schild der Ehre', icon: '🛡', desc: 'Aktiviere während einer Hand: Würdest du in dieser Hand ausscheiden, behältst du stattdessen 3 Big Blinds.' },
  zizzle: { id: 'magnet', name: 'Chipmagnet', icon: '🧲', desc: 'Aktiviere während einer Hand: Gewinnst du sie, zieht dein Magnet 10 % Bonus-Chips aus der Tavernenkasse (max. 200).' },
};

export const HEROES: Hero[] = [
  {
    id: 'brunhild', name: 'Brunhild Eisenfass', race: 'Zwergin · Braumeisterin', color: '#c8532b', style: 'Tight-aggressiv',
    ai: { tight: 0.7, aggr: 0.72, bluff: 0.15 },
    emotes: ['Prost! Auf ein ehrliches Blatt.', 'Hab Dank, Kumpel.', 'Sauber gespielt, das gönn ich dir.', 'Mein Geduldsfass ist gleich leer.'],
    skins: [
      { id: 'base', name: 'Braumeisterin', bg: ['#6b2f14', '#2a1108'], acc: '#e0762e' },
      { id: 'winter', name: 'Wintermarkt', bg: ['#2f4a6b', '#101b2a'], acc: '#9fd0ff' },
    ],
    ability: ABILITIES['brunhild']!,
  },
  {
    id: 'faelan', name: 'Faelan Nachtwind', race: 'Elf · Sternenmagier', color: '#6e7fd6', style: 'Berechnend',
    ai: { tight: 0.85, aggr: 0.5, bluff: 0.1 },
    emotes: ['Die Sterne grüßen dich.', 'Meinen Dank.', 'Elegant gelöst.', 'Die Wahrscheinlichkeit war nie auf deiner Seite.'],
    skins: [
      { id: 'base', name: 'Sternenmagier', bg: ['#2a2f6b', '#0d0f2a'], acc: '#9fb0ff' },
      { id: 'dawn', name: 'Morgenröte', bg: ['#7a2f4a', '#2a0f1a'], acc: '#ffb0c8' },
    ],
    ability: ABILITIES['faelan']!,
  },
  {
    id: 'grokk', name: 'Grokk Zahnbrecher', race: 'Ork · Arenakämpfer', color: '#5e8f3e', style: 'Loose-aggressiv',
    ai: { tight: 0.25, aggr: 0.9, bluff: 0.35 },
    emotes: ['GROKK GRÜSST!', 'Grokk dankt. Grokk vergisst nicht.', 'Du kämpfst gut, kleiner Mensch.', 'Grokk zermalmt deinen Stack!'],
    skins: [
      { id: 'base', name: 'Arenakämpfer', bg: ['#3e5a1e', '#14200a'], acc: '#c94a3a' },
      { id: 'iron', name: 'Eisenfaust', bg: ['#4a4a52', '#15151a'], acc: '#d0d4dc' },
    ],
    ability: ABILITIES['grokk']!,
  },
  {
    id: 'pippa', name: 'Pippa Flinkfuß', race: 'Halbling · Taschendiebin', color: '#d6a23a', style: 'Bluffkönigin',
    ai: { tight: 0.4, aggr: 0.6, bluff: 0.5 },
    emotes: ['Hallöchen! Pass gut auf deine Chips auf.', 'Danke – die behalte ich.', 'Ohh, gar nicht schlecht!', 'War das ein Bluff? Wer weiß …'],
    skins: [
      { id: 'base', name: 'Taschendiebin', bg: ['#7a5a1e', '#2a1e08'], acc: '#d94a3a' },
      { id: 'garden', name: 'Gartenfest', bg: ['#3e6b3a', '#122a12'], acc: '#ffd166' },
    ],
    ability: ABILITIES['pippa']!,
  },
  {
    id: 'aldric', name: 'Ser Aldric Morgenstern', race: 'Mensch · Paladin', color: '#e0c070', style: 'Geduldig',
    ai: { tight: 0.5, aggr: 0.25, bluff: 0.03 },
    emotes: ['Ehre sei diesem Tisch.', 'Habt Dank.', 'Ehrenvoll gespielt.', 'Die Gerechtigkeit callt.'],
    skins: [
      { id: 'base', name: 'Paladin', bg: ['#6b5a2a', '#2a2008'], acc: '#f0d080' },
      { id: 'night', name: 'Nachtwache', bg: ['#2a2a4a', '#0d0d1c'], acc: '#8090c0' },
    ],
    ability: ABILITIES['aldric']!,
  },
  {
    id: 'zizzle', name: 'Zizzle Funkenschraub', race: 'Goblin · Tüftlerin', color: '#3fb8a8', style: 'Chaotisch',
    ai: { tight: 0.3, aggr: 0.55, bluff: 0.4 },
    emotes: ['Zizzle ist online! Piep!', 'Dankeschön, kling-kling!', 'Beeindruckende Berechnung!', 'Alles nur ein Experiment.'],
    skins: [
      { id: 'base', name: 'Tüftlerin', bg: ['#1e5a55', '#081f1c'], acc: '#ff7ab0' },
      { id: 'copper', name: 'Kupferkessel', bg: ['#6b3a1e', '#2a1408'], acc: '#ffb066' },
    ],
    ability: ABILITIES['zizzle']!,
  },
];

export const HERO: Record<string, Hero> = Object.fromEntries(HEROES.map((h) => [h.id, h]));
